import cn from 'classnames'
import { classNames } from 'primereact/utils'
import React, { Ref, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { LockIcon } from '../../../lib/icons/lock-icon'
import { theme } from '../../../lib/theme'
import { KeyEvents } from '../../constants/KeyEventContants'
import useOnClickOutside from '../../hooks/useOnClickOutside'
import Input from '../Input'
import Tooltip from '../Tooltip'

import { Menu, MenuContainer, SArrowIcon, MenuItem } from './Combobox.styles'
import Portal from './Portal'

export type ComboboxValue = string | number | boolean
export type ComboboxOption<Value = ComboboxValue> = {
    value: Value
    label: string
}

export type Props<Value = ComboboxValue> = {
    id: string
    labelText?: string
    infoText?: string
    options?: ComboboxOption<Value>[]
    value?: Value
    error?: string
    onChange?: (option: ComboboxOption<Value>) => void
    disabled?: boolean
    disabledReason?: string
    enableDefaultOption?: boolean
    enableDefaultTextOption?: string
    wrapperClassName?: string
    ['data-testid']?: string
}

function Combobox<Value = ComboboxValue>(
    {
        options: propsOptions = [],
        value,
        onChange,
        disabled,
        disabledReason,
        enableDefaultOption = true,
        enableDefaultTextOption = '',
        wrapperClassName,
        ...props
    }: Props<Value>,
    ref?: Ref<HTMLInputElement>
): JSX.Element {
    const options = useMemo(() => (Array.isArray(propsOptions) ? propsOptions : []), [propsOptions])

    const [isOpen, setIsOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const dropdownMenuRef = useRef<HTMLUListElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    const [search, setSearch] = useState('')

    useOnClickOutside(dropdownMenuRef, (event: Event) => {
        if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false)
        setSearch('')
    })

    useEffect(() => {
        if (!dropdownMenuRef.current || !inputRef) {
            return
        }

        const inputBoundingRect = inputRef?.current?.getBoundingClientRect()
        const containerBoundingRect = containerRef?.current?.getBoundingClientRect()
        const menuBoundingRect = dropdownMenuRef?.current?.getBoundingClientRect()

        if (isOpen && containerBoundingRect && menuBoundingRect && inputBoundingRect) {
            dropdownMenuRef.current.style.width = `${containerBoundingRect.width}px`
            dropdownMenuRef.current.style.left = `${containerBoundingRect.x}px`
            dropdownMenuRef.current.style.top = `${inputBoundingRect.y + window.scrollY + inputBoundingRect.height + 3}px`
        }
    }, [isOpen])

    const activeOption = useMemo(() => {
        return options.find((o) => o.value === value)
    }, [options, value])

    const filteredOptions = useMemo(
        () =>
            (enableDefaultOption ? [{ value: null as Value, label: enableDefaultTextOption }] : []).concat(
                options.filter((item) => item?.label?.toLowerCase().includes(search.toLowerCase()))
            ),
        [enableDefaultOption, enableDefaultTextOption, options, search]
    )

    useImperativeHandle(ref, () => inputRef.current!, [])

    useEffect(() => {
        if (!inputRef.current) return

        if (isOpen) {
            inputRef.current.focus()
        } else {
            inputRef.current.blur()
        }
    }, [isOpen, ref])

    const renderMenu = () => {
        return (
            <Portal>
                <Menu
                    ref={dropdownMenuRef}
                    className={classNames({ open: isOpen })}
                    onKeyDown={(event) => {
                        if (isOpen && event.code === KeyEvents.ESCAPE) {
                            setIsOpen(false)
                            event.stopPropagation()
                        }
                    }}
                >
                    {filteredOptions.map((option, index) => (
                        <MenuItem
                            className={cn({
                                'default-text': enableDefaultOption,
                            })}
                            active={option.value === value}
                            key={`${index}-${option.value}`}
                            data-testid={`${
                                (props as Record<string, string>)['data-testid'] || 'combobox'
                            }-option-${index}`}
                            onTouchStart={(event) => {
                                event.stopPropagation()
                            }}
                            onClick={(event) => {
                                event.stopPropagation()
                                if (typeof onChange === 'function') {
                                    onChange(option)
                                }
                                setIsOpen(false)
                            }}
                            tabIndex={0}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </Menu>
            </Portal>
        )
    }

    return (
        <MenuContainer ref={containerRef} {...(wrapperClassName && { className: wrapperClassName })}>
            <Input
                value={isOpen ? search : activeOption?.label || ''}
                onChange={(event) => {
                    setSearch(event.target.value)
                }}
                onClick={() => {
                    if (!disabled) {
                        setIsOpen(!isOpen)
                    }
                }}
                onKeyDown={(event) => {
                    if (!isOpen && (event.code === KeyEvents.ARROW_DOWN || event.code === KeyEvents.ENTER)) {
                        setIsOpen(true)
                    } else if (isOpen && event.code === KeyEvents.ESCAPE) {
                        setIsOpen(false)
                        event.stopPropagation()
                    }
                }}
                autoComplete="off"
                disabled={disabled}
                {...props}
                ref={inputRef}
                dropdownMenu={
                    <>
                        <div className="flex align-items-center gap-1 px-3">
                            {disabled && disabledReason && (
                                <Tooltip
                                    content={disabledReason}
                                    data-testid={`${props['data-testid'] ?? 'combobox'}-disabled-reason`}
                                >
                                    <LockIcon
                                        size={15}
                                        fill={theme.brandColors.CA_Mid_Gray}
                                        data-testid={`${props['data-testid'] ?? 'combobox'}-lock-icon`}
                                    />
                                </Tooltip>
                            )}
                            <SArrowIcon
                                flipped={isOpen}
                                data-testid={`${props['data-testid'] ?? 'combobox'}-arrow-icon`}
                                color={disabled ? theme.brandColors.CA_Mid_Gray : theme.brandColors.CA_Black}
                                width={15}
                                height={6}
                            />
                        </div>
                        {renderMenu()}
                    </>
                }
            />
        </MenuContainer>
    )
}

export default forwardRef(Combobox) as typeof Combobox
